/**
 * Cash gifts — «Kontantgaver».  MKK module 22 (R2, owner 2026-10-29; MKK_PORTAL_FEATURE_ADOPTION.md §4.22).
 *
 * Somebody hands the mosque cash (in the box after jumu'ah, at the door, in an envelope). One staff member RECORDS it:
 * the amount in øre, the day it came in, the purpose (the donation purposes the app already offers), optionally the
 * campaign it was for (an id; the server checks it is open that day and gives the gift its purpose — campaignGift), optionally the giver's name and an e-mail or phone
 * for the receipt (both sealed at rest), and a note. A SECOND staff member CONFIRMS it (four eyes: never the one who
 * recorded it); confirming assigns the receipt number — per mosque, per year, in order: 2026-0001, 2026-0002 … — which
 * is never reused and never changed, and the receipt (a PDF) can then be handed out. Before confirmation an entry can be
 * rejected (a typo, a double count); after it, nothing changes.
 *
 * Bookkeeping rules (bokføringsloven, as the accountant will read them):
 *   - entries are append-only: amount, day, purpose, campaign never change, and a decided entry is frozen;
 *   - a correction is a NEW entry of kind 'reversal': the negative of the original amount, pointing at it, with a
 *     reason, recorded by one person and confirmed by another (the same four eyes) — at most one live reversal per gift;
 *   - a month can be LOCKED (period lock): no entry dated in it can be recorded or confirmed afterwards. A reversal is
 *     dated the day it is recorded, so a gift in a locked month is corrected in the open one.
 *
 * Not tax deduction (skattefradrag): the receipt says «Ikke grunnlag for skattefradrag», and nothing is reported to
 * Skatteetaten. That is its own legal project (MKK §4.22).
 *
 * Pure: no I/O, no clock — "today" (mosque-local, tz.dateIn) is passed in.
 */
const Day = require('./day');
const { oneLine, text, isId } = require('./tasks');
const Csv = require('./csv');
const { formatKr, kr } = require('./money');
const Donations = require('./donations');
const Campaigns = require('./campaigns');

const STATES = ['recorded', 'confirmed', 'rejected'];
const KINDS = ['gift', 'reversal'];
/**
 * The purposes are the donation purposes (`donations.PURPOSES`, the same list and names as Vipps gifts — fitrana and
 * langar included since 2026-10-29). `cash_gift.purpose`'s CHECK (066, 001_init, schema.sql, db.js) lists exactly these;
 * a purpose added to donations needs that CHECK widened too (cash-gifts.test.js pins the two lists together).
 */
const PURPOSES = Donations.PURPOSES;
const PURPOSE_NO = Donations.PURPOSE_NO;
const STATE_NO = { recorded: 'Registrert', confirmed: 'Bekreftet', rejected: 'Avvist' };
const KIND_NO = { gift: 'Kontantgave', reversal: 'Korrigering' };
const MAX_NOTE = 500;
const MAX_NAME = 120;
const MAX_CONTACT = 120;
const MAX_CAMPAIGN = 64;
/** Whole øre, more than nothing, at most 100 000 kr per entry: a larger cash sum is for the board and the bank, not this form. */
const MAX_AMOUNT_ORE = 100_000_00;
const MAX_ENTRIES = 50000;
/** The fields the recorder may send. */
// memberId (members register «pro», migration 075, 2026-10-31): optional — the person in the register the gift belongs to,
// picked by staff who may search the register; shown only on that person's page, never in the gift lists.
const FIELDS = ['amountOre', 'receivedOn', 'purpose', 'campaignId', 'donorName', 'donorContact', 'note', 'memberId'];
const TAX_NOTE = 'Ikke grunnlag for skattefradrag.';

const refuse = (code, message) => ({ ok: false, code, message });
const monthOf = (day) => String(day || '').slice(0, 7);
const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const validMonth = (m) => typeof m === 'string' && MONTH_RE.test(m);
const lockedSet = (locks) => new Set((locks || []).map((l) => (typeof l === 'string' ? l : l && l.month)).filter(Boolean));
/** Is this day in a locked month? `locks`: months ('YYYY-MM') or lock rows ({ month }). */
const isLocked = (day, locks) => lockedSet(locks).has(monthOf(day));

/** Whole øre from 1 to MAX_AMOUNT_ORE — never a fraction, never a string of digits. */
function amount(v) {
  if (!Number.isSafeInteger(v) || v <= 0) return { ok: false, message: 'Beløpet må være et helt antall øre, mer enn 0.' };
  if (v > MAX_AMOUNT_ORE) return { ok: false, message: `Beløpet kan være høyst ${kr(MAX_AMOUNT_ORE)} per registrering.` };
  return { ok: true, value: v };
}

/** A campaign's id, format only (letters, digits, - and _, at most 64; nothing = no campaign). Whether it is open: campaignGift. */
function campaignId(v) {
  if (v === undefined || v === null || v === '') return { ok: true, value: null };
  if (typeof v !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(v)) return { ok: false, message: 'Ugyldig kampanje.' };
  return { ok: true, value: v };
}

/**
 * A cash gift tied to a campaign (MKK round 2 review, 2026-10-29): the `campaigns` capability must be on (`on`), the
 * campaign must exist (`campaign`: core campaigns' shape, or null), be ACTIVE (not a draft, not closed) and the day the cash
 * came in must be within its first and last day. The gift then carries the campaign's gift purpose — the same mapping a
 * Vipps gift to a campaign gets (campaigns.DONATION_PURPOSE, apps/api campaigns.forGift) — whatever purpose was sent.
 * Only for a NEW gift: a reversal inherits its original's campaign and is never checked here (a correction to a closed
 * campaign must stay possible). → { ok: true, purpose } | { ok: false, code: 'campaign_not_open', message }
 */
function campaignGift(campaign, receivedOn, { on = false } = {}) {
  const shut = refuse('campaign_not_open', 'Innsamlingen tar ikke imot gaver for denne datoen. Registrer gaven uten innsamling, eller velg en annen.');
  if (!on || !campaign || campaign.status !== 'active') return shut;
  if (!Day.valid(receivedOn) || !Day.valid(campaign.startsOn) || !Day.valid(campaign.endsOn)) return shut;
  if (receivedOn < campaign.startsOn || receivedOn > campaign.endsOn) return shut;
  return { ok: true, purpose: Campaigns.DONATION_PURPOSE[campaign.purpose] || 'general' };
}

/**
 * The giver's e-mail or phone, for the receipt — optional. An e-mail (one @, a dot after it) is lower-cased; a phone is
 * digits with an optional leading +, 8 to 15 digits, spaces and dashes dropped. Anything else is refused.
 */
function contact(v) {
  if (v === undefined || v === null || v === '') return { ok: true, value: null };
  if (typeof v !== 'string') return { ok: false, message: 'Kontakten må være tekst.' };
  const s = v.trim();
  if (!s) return { ok: true, value: null };
  if (s.length > MAX_CONTACT) return { ok: false, message: `Kontakten kan ha maks ${MAX_CONTACT} tegn.` };
  if (s.includes('@')) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return { ok: false, message: 'Skriv en gyldig e-postadresse eller et telefonnummer.' };
    return { ok: true, value: s.toLowerCase() };
  }
  const p = s.replace(/[\s-]/g, '');
  if (!/^\+?\d{8,15}$/.test(p)) return { ok: false, message: 'Skriv en gyldig e-postadresse eller et telefonnummer.' };
  return { ok: true, value: p };
}

/**
 * A new cash gift as the recorder sends it — every field checked, unknown fields refused. `today`: the mosque's day (a
 * gift from tomorrow is a typo); `locks`: the locked months (a gift dated in one is refused).
 * → { ok, value: { amountOre, receivedOn, purpose, campaignId, donorName, donorContact, note[, memberId] } } | { ok: false, errors }
 */
function giftInput(input, { today, locks = [] } = {}) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { gift: 'Ugyldig registrering.' } };
  const unknown = Object.keys(src).filter((k) => !FIELDS.includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const errors = {}, out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  take('amountOre', amount(src.amountOre));
  const day = src.receivedOn;
  if (!Day.valid(day)) errors.receivedOn = 'Datoen må være en gyldig dato (ÅÅÅÅ-MM-DD).';
  else if (today && Day.valid(today) && Day.diff(today, day) > 0) errors.receivedOn = 'Datoen kan ikke være fram i tid.';
  else if (isLocked(day, locks)) errors.receivedOn = `Måneden ${monthOf(day)} er låst. Registrer gaven i en åpen måned, eller be regnskapsføreren om råd.`;
  else out.receivedOn = day;
  if (!PURPOSES.includes(src.purpose)) errors.purpose = 'Velg et formål.'; else out.purpose = src.purpose;
  take('campaignId', campaignId(src.campaignId));
  take('donorName', oneLine(src.donorName, 'giverens navn', MAX_NAME, false));
  take('donorContact', contact(src.donorContact));
  take('note', text(src.note, 'merknaden', MAX_NOTE));
  // Only when given: an entry without a person keeps the shape it always had.
  if (src.memberId !== undefined && src.memberId !== null && src.memberId !== '') {
    if (!isId(src.memberId)) errors.memberId = 'Ugyldig medlem.'; else out.memberId = src.memberId;
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, value: out };
}

/**
 * Confirm a recorded entry. Four eyes: never the one who recorded it. The entry's month must be open. A reversal is
 * confirmed only while its original is confirmed and no other reversal of it is. → { ok: true } | { ok: false, code, message }
 */
function confirmation(entry, { actorId, locks = [], original = null, otherReversals = [] } = {}) {
  if (!entry || !isId(actorId)) return refuse('not_found', 'Fant ikke registreringen.');
  if (entry.recordedBy === actorId) return refuse('four_eyes', 'Du kan ikke bekrefte det du selv har registrert. En annen må gjøre det.');
  if (entry.status !== 'recorded') return refuse('not_recorded', `Registreringen er «${STATE_NO[entry.status] || entry.status}» og kan ikke bekreftes.`);
  if (isLocked(entry.receivedOn, locks)) return refuse('period_locked', `Måneden ${monthOf(entry.receivedOn)} er låst. Registreringen kan ikke bekreftes.`);
  if (entry.kind === 'reversal') {
    if (!original || original.kind !== 'gift' || original.status !== 'confirmed') return refuse('original_not_confirmed', 'Gaven som korrigeres er ikke bekreftet.');
    if (otherReversals.some((r) => r.id !== entry.id && r.status === 'confirmed')) return refuse('already_reversed', 'Gaven er allerede korrigert.');
  }
  return { ok: true };
}

/**
 * Reject an entry before it is confirmed: by the one who recorded it (their own typo) or by someone who confirms. Somebody
 * else's entry needs a reason. → { ok, value: { decisionNote } } | { ok: false, code, message }
 */
function rejection(entry, { actorId, note, mayConfirm = false } = {}) {
  if (!entry || !isId(actorId)) return refuse('not_found', 'Fant ikke registreringen.');
  const own = entry.recordedBy === actorId;
  if (!own && !mayConfirm) return refuse('not_permitted', 'Bare den som registrerte, eller en som bekrefter, kan avvise.');
  if (entry.status !== 'recorded') return refuse('not_recorded', `Registreringen er «${STATE_NO[entry.status] || entry.status}» og kan ikke avvises.`);
  const n = text(note, 'en begrunnelse', MAX_NOTE, !own);
  if (!n.ok) return refuse('invalid', n.message);
  return { ok: true, value: { decisionNote: n.value } };
}

/**
 * A correction of a confirmed gift: a new entry, the negative of the original amount, the same purpose and campaign,
 * dated `today` (the correction is booked in the open period), with a reason. It is RECORDED here and confirmed by
 * another person like any entry. Refused for a reversal, an unconfirmed gift, a gift that already has a live reversal
 * (recorded or confirmed), and when today's month is locked.
 * → { ok, value: { kind, reversesId, amountOre, receivedOn, purpose, campaignId, note } } | { ok: false, code, message }
 */
function reversalInput(original, { reason, today, locks = [], reversals = [] } = {}) {
  if (!original) return refuse('not_found', 'Fant ikke gaven.');
  if (original.kind !== 'gift') return refuse('not_a_gift', 'En korrigering kan ikke korrigeres. Registrer en ny gave i stedet.');
  if (original.status !== 'confirmed') return refuse('not_confirmed', 'Bare en bekreftet gave korrigeres. En registrering som ikke er bekreftet, avvises.');
  if (reversals.some((r) => r.status !== 'rejected')) return refuse('already_reversed', 'Gaven har allerede en korrigering.');
  if (!Day.valid(today)) return refuse('invalid', 'Ugyldig dato.');
  if (isLocked(today, locks)) return refuse('period_locked', `Måneden ${monthOf(today)} er låst. En korrigering kan ikke registreres nå.`);
  const r = text(reason, 'hvorfor gaven korrigeres', MAX_NOTE, true);
  if (!r.ok) return refuse('invalid', r.message);
  return { ok: true, value: { kind: 'reversal', reversesId: original.id, amountOre: -original.amountOre, receivedOn: today,
    purpose: original.purpose, campaignId: original.campaignId || null, note: r.value } };
}

/** A month to lock: «YYYY-MM», not in the future, not already locked, and nothing in it still waiting for confirmation. */
function lockInput(month, { today, locks = [], pending = 0 } = {}) {
  if (!validMonth(month)) return refuse('invalid', 'Oppgi måneden som ÅÅÅÅ-MM.');
  if (today && month > monthOf(today)) return refuse('invalid', 'En måned fram i tid kan ikke låses.');
  if (lockedSet(locks).has(month)) return refuse('already_locked', `Måneden ${month} er allerede låst.`);
  if (pending > 0) return refuse('pending_entries', `${pending} registrering(er) i ${month} venter på bekreftelse. Bekreft eller avvis dem først.`);
  return { ok: true, value: { month } };
}

/** The receipt number: the year and four digits (2026-0001); a fifth digit only after 9999 in one year. */
function receiptNumber(year, seq) {
  if (!Number.isSafeInteger(year) || year < 2000 || year > 9999 || !Number.isSafeInteger(seq) || seq < 1) throw new Error('cash-gifts: bad receipt number');
  return `${year}-${String(seq).padStart(4, '0')}`;
}
/** The next number in `year`, given the highest sequence already used in it (0 or null for none). */
function nextReceipt(year, maxSeq) {
  const seq = (Number(maxSeq) || 0) + 1;
  return { year, seq, number: receiptNumber(year, seq) };
}

/** The list filter: month (YYYY-MM), purpose, state (recorded | confirmed | rejected | all; default all). Unknown values refused. */
const STATE_FILTERS = ['all', ...STATES];
function listFilter(q = {}) {
  const v = (x) => (x === undefined || x === null || x === '' ? null : String(x));
  const month = v(q.month), purpose = v(q.purpose), state = v(q.state) || 'all';
  if (month !== null && !validMonth(month)) return { ok: false, message: 'Ugyldig måned (ÅÅÅÅ-MM).' };
  if (purpose !== null && !PURPOSES.includes(purpose)) return { ok: false, message: 'Ukjent formål.' };
  if (!STATE_FILTERS.includes(state)) return { ok: false, message: 'Ugyldig statusfilter.' };
  return { ok: true, value: { month, purpose, state } };
}
const matches = (e, f) => (!f.month || monthOf(e.receivedOn) === f.month) && (!f.purpose || e.purpose === f.purpose) && (f.state === 'all' || e.status === f.state);
/** Waiting first, then the newest day first; ties end on the id so two pages never swap a row. */
function sort(entries) {
  const rank = (e) => (e.status === 'recorded' ? 0 : 1);
  return [...entries].sort((a, b) => rank(a) - rank(b) || String(b.receivedOn).localeCompare(String(a.receivedOn))
    || String(b.recordedAt || '').localeCompare(String(a.recordedAt || '')) || String(a.id).localeCompare(String(b.id)));
}

/** Only confirmed entries count as money. */
const counted = (rows) => (rows || []).filter((e) => e && e.status === 'confirmed' && Number.isSafeInteger(e.amountOre));

/**
 * Totals per month and purpose, confirmed entries only, net of reversals (a reversal is booked in its own month).
 * → [{ month, purpose, ore, gifts, reversals }] by month, then purpose in PURPOSES order.
 */
function totals(rows) {
  const by = new Map();
  for (const e of counted(rows)) {
    const k = `${monthOf(e.receivedOn)}|${e.purpose}`;
    const t = by.get(k) || { month: monthOf(e.receivedOn), purpose: e.purpose, ore: 0, gifts: 0, reversals: 0 };
    t.ore += e.amountOre;
    if (e.kind === 'reversal') t.reversals += 1; else t.gifts += 1;
    by.set(k, t);
  }
  return [...by.values()].sort((a, b) => a.month.localeCompare(b.month) || PURPOSES.indexOf(a.purpose) - PURPOSES.indexOf(b.purpose));
}

/**
 * For the campaigns branch: what cash has come in for one campaign — confirmed entries with that campaign id, net of
 * confirmed reversals (a reversal carries its original's campaign). `gifts` counts confirmed gifts that are not reversed.
 * → { campaignId, ore, gifts }
 */
function campaignCashTotals(rows, id) {
  const mine = counted(rows).filter((e) => id && e.campaignId === id);
  const reversed = new Set(mine.filter((e) => e.kind === 'reversal').map((e) => e.reversesId));
  return { campaignId: id || null, ore: mine.reduce((s, e) => s + e.amountOre, 0),
    gifts: mine.filter((e) => e.kind === 'gift' && !reversed.has(e.id)).length };
}

/** The export's period (by the day received): both days, from ≤ to; the default is this year up to today (day.js). */
const period = Day.period;

/**
 * The accountant's file: one line per CONFIRMED entry — the day, the receipt number, the kind, what it corrects, the
 * purpose, the campaign, the amount (kr with a comma, no unit, no grouping; negative for a correction), who recorded,
 * who confirmed and when (the mosque's day, computed by the caller as `confirmedOn`). The giver's name only with
 * `withDonor` (the caller audits that it was asked for); never the e-mail or phone. Semicolons, CRLF, quoted as needed.
 */
// «Bekreftet med Vipps» (owner 2026-10-29): «ja 2026-10-29 14:05» when the confirmation was made with Vipps (and still
// counts), else «nei» — computed by the caller (vipps-signature.csvValue); never a name or a phone.
const CSV_HEAD = ['Dato mottatt', 'Kvitteringsnr', 'Type', 'Korrigerer', 'Formål', 'Kampanje', 'Beløp (kr)', 'Registrert av', 'Bekreftet av', 'Bekreftet dato', 'Bekreftet med Vipps'];
// Typed text through Csv.cell (a formula start gets an apostrophe, so Excel shows the text instead of running it). The
// amount is a number the accountant sums, through Csv.num: a minus sign is kept (it is ours, not a formula someone typed).
function csvLine(e, { withDonor = false } = {}) {
  const cells = [e.receivedOn, e.receiptNo || '', KIND_NO[e.kind] || e.kind, e.reversesReceiptNo || '', PURPOSE_NO[e.purpose] || e.purpose,
    e.campaignId || '', e.recordedByName || '', e.decidedByName || '', e.confirmedOn || '', e.vippsConfirmed || 'nei'].map(Csv.cell);
  cells.splice(6, 0, Csv.num(formatKr(e.amountOre, { grouped: false })));
  if (withDonor) cells.push(Csv.cell(e.donorName || ''));
  return Csv.line(cells);
}
function csv(entries, { withDonor = false } = {}) {
  return Csv.table([...CSV_HEAD, ...(withDonor ? ['Giver'] : [])], entries.map((e) => csvLine(e, { withDonor })));
}

/** «12.10.2026» from «2026-10-12». */
const dmy = (day) => (Day.valid(day) ? `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}` : String(day || ''));

/**
 * The receipt's lines for receipt-pdf.js (the first is the heading). WinAnsi only: the PDF writer turns anything else into
 * «?», so purpose names are Latin. `entry` is a confirmed gift; `reversal` its confirmed reversal, if any.
 */
function receiptLines({ mosqueName, orgNo = null, entry, confirmedByName, confirmedOn, reversal = null, vippsLine = null }) {
  const lines = [
    `Kvittering for kontantgave ${entry.receiptNo}`,
    `${mosqueName || 'Moskeen'}${orgNo ? ` (org.nr. ${orgNo})` : ''}`,
    '',
    `Kvitteringsnummer: ${entry.receiptNo}`,
    `Dato mottatt: ${dmy(entry.receivedOn)}`,
    `Beløp: ${formatKr(entry.amountOre, { grouped: false })} kr`,
    `Formål: ${PURPOSE_NO[entry.purpose] || entry.purpose}`,
    'Betalingsmåte: Kontantgave',
  ];
  if (entry.donorName) lines.push(`Giver: ${entry.donorName}`);
  lines.push(`Bekreftet av: ${confirmedByName || '-'}${confirmedOn ? ` (${dmy(confirmedOn)})` : ''}`);
  // «Bekreftet med Vipps» (owner 2026-10-29): the confirmer's line from vipps-signature.stampLine(), when confirmed so.
  if (vippsLine) lines.push(vippsLine);
  lines.push('', 'Gaven er talt og registrert av to personer i moskeen.', TAX_NOTE);
  if (reversal) lines.push('', `ANNULLERT: denne gaven er korrigert (bokført ${dmy(reversal.receivedOn)}). Kvitteringen gjelder ikke lenger.`);
  lines.push('', 'Takk for gaven.');
  return lines;
}

/**
 * What the activity log keeps of an entry: amount, day, purpose, campaign id, kind, state, receipt number, what it
 * corrects. NEVER the giver's name or contact, the note, a reason or a decision note (people's words, and a giver's
 * identity). Every key starts with cash_ (core audit.js CASH_FIELDS, cashAudit()).
 */
function auditView(e = {}) {
  const out = {};
  if (e.amountOre !== undefined) out.cash_amount_ore = e.amountOre;
  if (e.receivedOn !== undefined) out.cash_received_on = e.receivedOn;
  if (e.purpose !== undefined) out.cash_purpose = e.purpose;
  if (e.campaignId !== undefined && e.campaignId !== null) out.cash_campaign = e.campaignId;
  if (e.kind !== undefined) out.cash_kind = e.kind;
  if (e.status !== undefined) out.cash_state = e.status;
  if (e.receiptNo !== undefined && e.receiptNo !== null) out.cash_receipt_no = e.receiptNo;
  if (e.reversesId !== undefined && e.reversesId !== null) out.cash_reverses = e.reversesId;
  return out;
}

module.exports = { campaignGift, STATES, KINDS, PURPOSES, PURPOSE_NO, STATE_NO, KIND_NO, STATE_FILTERS, FIELDS, TAX_NOTE, CSV_HEAD,
  MAX_NOTE, MAX_NAME, MAX_CONTACT, MAX_CAMPAIGN, MAX_AMOUNT_ORE, MAX_ENTRIES,
  isId, monthOf, validMonth, isLocked, amount, campaignId, contact, giftInput, confirmation, rejection, reversalInput, lockInput,
  receiptNumber, nextReceipt, listFilter, matches, sort, totals, campaignCashTotals, period, csvLine, csv, receiptLines, auditView,
  formatKr, kr };
